/**
 * Device-settings bridge for the hosted-mode meeting popup (SM-1187 follow-up).
 *
 * In MF/hosted mode the Meet Settings dialog runs on the TeamHub host origin
 * while the meeting runs in a popup served from NG Meet's own origin. Those are
 * separate origins with separate storage, and `MediaDeviceInfo.deviceId` is
 * salted per origin — so device selection can only be bridged by *label*.
 *
 * This module reads/writes the current origin's persisted selection in terms of
 * labels, so the opener can hand the host selection to the popup, and the popup
 * can hand its final in-meeting selection back to the host. Applying by label
 * (rather than id) is the same approach the in-meeting apply uses (SM-1151).
 */

import {
  findDeviceByStoredLabel,
  getPersistedDeviceSelections,
  resolveSelectionLabels,
  setStoredJitsiDeviceLabels,
} from '../../utils/deviceUtils';
import { settingsService } from '../settingsService';
import type { MeetingWindowDeviceLabels } from './protocol';

/**
 * Resolve the current origin's persisted device selection to labels, for
 * sending across the origin boundary. Falls back to enumeration for any
 * selection whose label isn't already cached in jitsiLocalStorage.
 */
export async function getHostDeviceLabels(): Promise<MeetingWindowDeviceLabels> {
  const labels = await resolveSelectionLabels(getPersistedDeviceSelections());
  return {
    cameraLabel: labels.camera ?? null,
    microphoneLabel: labels.microphone ?? null,
    audioOutputLabel: labels.audioOutput ?? null,
  };
}

const hasAnyLabel = (labels: MeetingWindowDeviceLabels): boolean =>
  !!(labels.cameraLabel || labels.microphoneLabel || labels.audioOutputLabel);

/**
 * Apply bridged device labels to the current origin's storage: store the labels
 * verbatim (so label-based apply/recovery has them) and, where the physical
 * device is present at this origin, resolve each label to its origin-local
 * deviceId and persist the selection through `settingsService`.
 *
 * Persisting through `settingsService` (with `persist: true`) both writes the
 * flat device keys / jitsiLocalStorage ids and notifies listeners, so an open
 * Meet Settings panel refreshes.
 */
async function applyDeviceLabelsToCurrentOrigin(labels: MeetingWindowDeviceLabels): Promise<void> {
  if (!hasAnyLabel(labels)) {
    return;
  }

  setStoredJitsiDeviceLabels({
    camera: labels.cameraLabel ?? undefined,
    microphone: labels.microphoneLabel ?? undefined,
    audioOutput: labels.audioOutputLabel ?? undefined,
  });

  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return;
  }

  let devices: MediaDeviceInfo[];
  try {
    devices = await navigator.mediaDevices.enumerateDevices();
  } catch {
    return;
  }

  const camId = findDeviceByStoredLabel(
    devices,
    'videoinput',
    labels.cameraLabel ?? undefined,
  )?.deviceId;
  const micId = findDeviceByStoredLabel(
    devices,
    'audioinput',
    labels.microphoneLabel ?? undefined,
  )?.deviceId;
  const spkId = findDeviceByStoredLabel(
    devices,
    'audiooutput',
    labels.audioOutputLabel ?? undefined,
  )?.deviceId;

  if (!(camId || micId || spkId)) {
    return;
  }

  const current = settingsService.getSettings().devices;
  await settingsService.updateSettings(
    {
      devices: {
        ...current,
        ...(camId && { selectedCamera: camId }),
        ...(micId && { selectedMicrophone: micId }),
        ...(spkId && { selectedAudioOutput: spkId }),
      },
    },
    { validate: true, persist: true },
  );
}

/** Opener side: write a popup's in-meeting selection into the host settings. */
export const applyDeviceLabelsToHostSettings = applyDeviceLabelsToCurrentOrigin;

/** Popup side: seed the host's selection into the popup so the meeting uses it. */
export const applyDeviceLabelsToPopupStorage = applyDeviceLabelsToCurrentOrigin;
